// src/components/Onboarding/CustomerOnboarding/customerApi.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

// Customer onboarding API uses the main API Gateway endpoint
// Updated to use api.innoworx.ai to match invoice API and ensure data consistency
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Helper function to make authenticated API requests
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    // Get authentication token
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('No authentication token available');
    }

    // Trim token to remove any whitespace that might cause header parsing issues
    const token = idToken.trim();

    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    console.log(`Customer API: Making ${method} request to: ${url}`);
    if (body) {
      console.log('Customer API: Request payload:', body);
    }

    let response;
    try {
      response = await fetch(url, options);
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
        // This could be a preflight (OPTIONS) issue or general CORS issue
        const corsError = new Error('CORS Error: Unable to connect to the API. This may be a preflight (OPTIONS) request issue or backend CORS configuration issue.');
        corsError.isCorsError = true;
        corsError.isPreflightIssue = true; // Flag as potential preflight issue
        corsError.status = 0;
        corsError.method = method;
        corsError.endpoint = endpoint;
        console.error('Customer API: CORS/Preflight Error Details:', {
          method,
          endpoint,
          url,
          error: fetchError.message,
          suggestion: 'Backend needs to handle OPTIONS requests and return proper CORS headers (Access-Control-Allow-Origin, Access-Control-Allow-Methods, Access-Control-Allow-Headers)',
        });
        
        // Log network/fetch errors to logging service
        loggingService.logError(
          'api_error_customer',
          `Customer API network/fetch error for ${endpoint}`,
          corsError,
          {
            endpoint,
            method,
            errorType: corsError.name || 'NetworkError',
            isCorsError: true,
            isPreflightIssue: true,
          }
        ).catch(logError => {
          console.warn('[customerApi] Failed to log error:', logError);
        });
        
        throw corsError;
      }
      
      // Log network/fetch errors to logging service
      loggingService.logError(
        'api_error_customer',
        `Customer API network/fetch error for ${endpoint}`,
        fetchError,
        {
          endpoint,
          method,
          errorType: fetchError?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[customerApi] Failed to log error:', logError);
      });
      
      throw fetchError;
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || `HTTP error! status: ${response.status}` };
      }

      const error = new Error(errorData.message || `API Error: ${response.status}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_customer',
        `Customer API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[customerApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await response.json();
    console.log('Customer API: Response:', data);
    return data;
  } catch (error) {
    console.error(`Customer API: Error in ${method} ${endpoint}:`, error);
    if (error.isCorsError) {
      throw error;
    }
    if (error.status) {
      throw error;
    }
    throw new Error(error.message || 'Network error');
  }
};

/**
 * Clean object by removing undefined, null, and empty string values
 */
const cleanObject = (obj) => {
  if (obj === null || obj === undefined) {
    return undefined;
  }

  if (Array.isArray(obj)) {
    const cleaned = obj
      .map((item) => cleanObject(item))
      .filter((item) => item !== undefined && item !== null);
    return cleaned.length > 0 ? cleaned : undefined;
  }

  if (typeof obj === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = cleanObject(value);
      if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '') {
        cleaned[key] = cleanedValue;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  return obj;
};

export const customerApi = {
  /**
   * Create a new customer
   * POST /billing/customers
   */
  createCustomer: async (customerData) => {
    try {
      const cleanedData = cleanObject(customerData);
      const response = await makeApiRequest('/billing/customers', 'POST', cleanedData);

      return {
        success: true,
        customer: response.customer,
        message: response.message || 'Customer created successfully',
      };
    } catch (error) {
      console.error('Customer API: Error in createCustomer:', error);

      if (error.status === 401) {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      if (error.isCorsError) {
        return {
          success: false,
          error: 'CORS Error',
          message: error.isPreflightIssue 
            ? 'CORS Preflight Error: The browser is unable to complete the preflight (OPTIONS) request. This is likely a backend CORS configuration issue. The backend needs to handle OPTIONS requests and return proper CORS headers.'
            : 'Unable to connect to the API server. This is likely a backend CORS configuration issue. Please contact your system administrator.',
          isPreflightIssue: error.isPreflightIssue,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to create customer',
      };
    }
  },

  /**
   * List customers with optional filters
   * GET /billing/customers
   */
  listCustomers: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);
      if (params.site_id) queryParams.append('site_id', params.site_id);
      if (params.last_evaluated_key) queryParams.append('last_evaluated_key', params.last_evaluated_key);

      const queryString = queryParams.toString();
      const endpoint = `/billing/customers${queryString ? `?${queryString}` : ''}`;

      const response = await makeApiRequest(endpoint, 'GET');

      return {
        success: true,
        customers: response.customers || [],
        next_token: response.next_token || null,
      };
    } catch (error) {
      console.error('Customer API: Error in listCustomers:', error);

      if (error.status === 401) {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          customers: [],
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          customers: [],
        };
      }

      if (error.isCorsError) {
        return {
          success: false,
          error: 'CORS Error',
          message: error.isPreflightIssue 
            ? 'CORS Preflight Error: The browser is unable to complete the preflight (OPTIONS) request. This is likely a backend CORS configuration issue.'
            : 'Unable to connect to the API server. This is likely a backend CORS configuration issue.',
          customers: [],
          isPreflightIssue: error.isPreflightIssue,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch customers',
        customers: [],
      };
    }
  },

  /**
   * Get a single customer by ID
   * GET /billing/customers/{customer_id}
   */
  getCustomer: async (customerId) => {
    try {
      const response = await makeApiRequest(`/billing/customers/${customerId}`, 'GET');

      return {
        success: true,
        customer: response.customer,
      };
    } catch (error) {
      console.error('Customer API: Error in getCustomer:', error);

      if (error.status === 401) {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status === 404) {
        return {
          success: false,
          error: 'Not found',
          message: 'Customer not found',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      if (error.isCorsError) {
        return {
          success: false,
          error: 'CORS Error',
          message: error.isPreflightIssue 
            ? 'CORS Preflight Error: The browser is unable to complete the preflight (OPTIONS) request. This is likely a backend CORS configuration issue.'
            : 'Unable to connect to the API server. This is likely a backend CORS configuration issue.',
          isPreflightIssue: error.isPreflightIssue,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch customer',
      };
    }
  },

  /**
   * Update a customer
   * PUT /billing/customers/{customer_id}
   */
  updateCustomer: async (customerId, customerData) => {
    try {
      const cleanedData = cleanObject(customerData);
      const response = await makeApiRequest(`/billing/customers/${customerId}`, 'PUT', cleanedData);

      return {
        success: true,
        customer: response.customer,
        message: response.message || 'Customer updated successfully',
      };
    } catch (error) {
      console.error('Customer API: Error in updateCustomer:', error);

      if (error.status === 401) {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status === 404) {
        return {
          success: false,
          error: 'Not found',
          message: 'Customer not found',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      if (error.isCorsError) {
        return {
          success: false,
          error: 'CORS Error',
          message: error.isPreflightIssue 
            ? 'CORS Preflight Error: The browser is unable to complete the preflight (OPTIONS) request. This is likely a backend CORS configuration issue.'
            : 'Unable to connect to the API server. This is likely a backend CORS configuration issue.',
          isPreflightIssue: error.isPreflightIssue,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to update customer',
      };
    }
  },

  /**
   * Delete (soft delete) a customer
   * DELETE /billing/customers/{customer_id}
   */
  deactivateCustomer: async (customerId) => {
    try {
      const response = await makeApiRequest(`/billing/customers/${customerId}`, 'DELETE');

      return {
        success: true,
        customer: response.customer,
        message: response.message || 'Customer deactivated successfully',
      };
    } catch (error) {
      console.error('Customer API: Error in deactivateCustomer:', error);

      if (error.status === 401) {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status === 404) {
        return {
          success: false,
          error: 'Not found',
          message: 'Customer not found',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      if (error.isCorsError) {
        return {
          success: false,
          error: 'CORS Error',
          message: error.isPreflightIssue 
            ? 'CORS Preflight Error: The browser is unable to complete the preflight (OPTIONS) request. This is likely a backend CORS configuration issue.'
            : 'Unable to connect to the API server. This is likely a backend CORS configuration issue.',
          isPreflightIssue: error.isPreflightIssue,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to deactivate customer',
      };
    }
  },

  /**
   * Get presigned URL for logo upload
   * POST /billing/customers/{customer_id}/logo/presign
   */
  presignLogoUpload: async (customerId, contentType = 'image/jpeg') => {
    try {
      const endpoint = `/billing/customers/${customerId}/logo/presign?content_type=${encodeURIComponent(contentType)}`;
      const response = await makeApiRequest(endpoint, 'POST');

      return {
        success: true,
        presigned_post: response.presigned_post,
        object_key: response.object_key,
        bucket: response.bucket,
        content_type: response.content_type,
        expires_in: response.expires_in,
      };
    } catch (error) {
      console.error('Customer API: Error in presignLogoUpload:', error);

      if (error.status === 401) {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status === 404) {
        return {
          success: false,
          error: 'Not found',
          message: 'Customer not found',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      if (error.isCorsError) {
        return {
          success: false,
          error: 'CORS Error',
          message: error.isPreflightIssue 
            ? 'CORS Preflight Error: The browser is unable to complete the preflight (OPTIONS) request. This is likely a backend CORS configuration issue.'
            : 'Unable to connect to the API server. This is likely a backend CORS configuration issue.',
          isPreflightIssue: error.isPreflightIssue,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to get presigned URL',
      };
    }
  },
};

